// Live cockpit derivations (RESTYLE §3).
//   runClock(input) → RunClock      the hero clock: time since T0 against the timeout once the sample runs; before T0 the
//                                   current phase's own clock (from its job.state event, else the record's dispatch or
//                                   submit time, so a dispatched run without events still ticks); frozen at collection
// (The events-per-second sparkline reads the event store's per-second histogram: stream.rate.series(now).)
import type { Transition } from "../../lib/derive/types";
import type { DisplayState } from "./lifecycle";

export type RunClockInput = {
  now: number;
  t0: number | null;
  t0Source: "running" | "booting" | null;
  phase: DisplayState;
  /** Epoch ms the current phase began (facts.since). */
  since: number | null;
  transitions: readonly Transition[];
  timeoutMs: number;
  dispatchedAt?: string;
  submittedAt?: string;
};

export type RunClock = {
  /** T0 is the sample's start: the hero is the run clock. Otherwise it counts the current phase. */
  running: boolean;
  phase: DisplayState;
  elapsedMs: number;
  timeoutMs: number;
  /** elapsed / timeout in 0–1 (0 before T0). */
  progress: number;
  /** Time past the timeout while the run still streams. */
  overMs: number;
  /** Time since collection began (collecting only). */
  collectingMs: number | null;
  /** Epoch ms the hero counts from. */
  from: number | null;
};

const finite = (n: number | null | undefined): n is number => typeof n === "number" && Number.isFinite(n);

function parse(s?: string): number | null {
  const v = s ? Date.parse(s) : NaN;
  return Number.isFinite(v) ? v : null;
}

export function runClock(i: RunClockInput): RunClock {
  const timeoutMs = Math.max(0, i.timeoutMs || 0);
  if (i.t0Source === "running" && finite(i.t0)) {
    const t0 = i.t0;
    const c = i.transitions.find((t) => t.state === "collecting");
    const collectAt = i.phase === "collecting" && c && finite(c.ts) && c.ts >= t0 ? c.ts : null;
    const elapsedMs = Math.max(0, (collectAt ?? i.now) - t0);
    return {
      running: true,
      phase: i.phase,
      elapsedMs,
      timeoutMs,
      progress: timeoutMs > 0 ? Math.min(1, elapsedMs / timeoutMs) : 0,
      overMs: collectAt === null && timeoutMs > 0 ? Math.max(0, elapsedMs - timeoutMs) : 0,
      collectingMs: collectAt !== null ? Math.max(0, i.now - collectAt) : null,
      from: t0,
    };
  }
  const from = (finite(i.since) ? i.since : null) ?? parse(i.dispatchedAt) ?? parse(i.submittedAt);
  return {
    running: false,
    phase: i.phase,
    elapsedMs: from !== null ? Math.max(0, i.now - from) : 0,
    timeoutMs,
    progress: 0,
    overMs: 0,
    collectingMs: null,
    from,
  };
}

